import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { type ColumnDef, DataTable } from '@/components/common/data-table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  collectVersions,
  type OnFarmImplantFreshEmbryo,
  OnFarmImplantRequestError,
  useDiscardOnFarmImplantFreshEmbryos,
  useRecoverOnFarmImplantFreshEmbryos,
} from '@/services/on-farm-implant';

export type FreshEmbryoAction = 'discard' | 'recover';

type FreshEmbryoActionDialogProps = {
  action: FreshEmbryoAction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  embryos: OnFarmImplantFreshEmbryo[];
  onDone: (updated: OnFarmImplantFreshEmbryo[]) => void;
};

export function FreshEmbryoActionDialog({
  action,
  open,
  onOpenChange,
  sessionId,
  embryos,
  onDone,
}: FreshEmbryoActionDialogProps) {
  const { t } = useTranslation();
  const discard = useDiscardOnFarmImplantFreshEmbryos();
  const recover = useRecoverOnFarmImplantFreshEmbryos();
  const mutation = action === 'discard' ? discard : recover;
  const [error, setError] = useState<string | null>(null);
  const keys = `onFarmImplant.fresh.${action}`;

  const toErrorMessage = (actionError: unknown) => {
    if (!(actionError instanceof OnFarmImplantRequestError))
      return t(`${keys}.failed`);
    if (actionError.problems.length) return actionError.problems.join(' ');
    if (actionError.status === 403) return t(`${keys}.forbidden`);
    return t(`${keys}.failedStatus`, { status: actionError.status });
  };

  const confirm = async () => {
    try {
      const updated = await mutation.mutateAsync({
        sessionId,
        sessionEmbryoIds: embryos.map((embryo) => embryo.sessionEmbryoId),
        versions: collectVersions(embryos),
      });
      setError(null);
      onDone(updated);
      onOpenChange(false);
    } catch (actionError) {
      setError(toErrorMessage(actionError));
    }
  };

  const columns: ColumnDef<OnFarmImplantFreshEmbryo>[] = [
    {
      key: 'strawNumber',
      header: t('onFarmImplant.fresh.straw'),
      width: 90,
      render: (embryo) => embryo.strawNumber ?? '—',
    },
    {
      key: 'matingName',
      header: t('onFarmImplant.fresh.mating'),
      width: 200,
      render: (embryo) => embryo.matingName ?? '—',
    },
    {
      key: 'stage',
      header: t('onFarmImplant.fresh.stage'),
      width: 90,
      render: (embryo) => embryo.stage ?? t('onFarmImplant.fresh.unknownStage'),
    },
    {
      key: 'grade',
      header: t('onFarmImplant.fresh.gradeLabel'),
      width: 90,
      render: (embryo) => embryo.grade,
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setError(null);
        onOpenChange(next);
      }}
      modal
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(`${keys}.title`, { count: embryos.length })}
          </DialogTitle>
          <DialogDescription>{t(`${keys}.description`)}</DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <DataTable
            rows={embryos}
            columns={columns}
            getRowId={(embryo) => embryo.sessionEmbryoId}
            storageKey={`on-farm-implant-fresh-${action}-preview`}
            fillWidth
          />
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t(`${keys}.cancel`)}
          </Button>
          <Button
            type="button"
            variant={action === 'discard' ? 'destructive' : 'default'}
            disabled={mutation.isPending || embryos.length === 0}
            onClick={() => void confirm()}
          >
            {t(`${keys}.confirm`, { count: embryos.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
